import React, { useRef } from 'react';
import { Rnd } from 'react-rnd';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  InfoWindow,
} from '@vis.gl/react-google-maps';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { API } from '../../config';
import Buttons from '../Button';
import '../DnDTable/DnDTable.scss';
import './joblinkMap.scss';

const FloatingMap = ({
  isVisible,
  onClose,
  currentLocation,
  geocodedJobs,
  selectedPlace,
  showingInfoWindow,
  onMarkerClick,
  onInfoWindowClose,
  getMarkerColor,
  markerRefs,
  startGlobalDrag,
  mapTaskDndSource,
  handleClickJobListItem,
  toggleCoachmarksVisibility,
  currentCoachmarkTarget,
  onMapReady,
  shouldFitOnReady,
}) => {
  const mapRef = useRef(null);
  const handleResize = () => {
    if (mapRef.current) {
      setTimeout(() => {
        globalThis.google?.maps?.event?.trigger(mapRef.current, 'resize');
      }, 100);
    }
  };

  if (!isVisible) return null;

  return createPortal(
    <Rnd
      default={{
        x: window.innerWidth * 0.15,
        y: window.innerHeight * 0.15,
        width: window.innerWidth * 0.7,
        height: window.innerHeight * 0.7,
      }}
      minWidth={400}
      minHeight={300}
      bounds="window"
      dragHandleClassName="drag-handle"
      onResize={handleResize}
      style={{
        zIndex: 9999,
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
      }}
    >
      <div className="w-full h-full flex flex-col">
        <div className="drag-handle flex justify-between items-center p-2 bg-gray-100 cursor-move rounded-t-xl">
          <h1 className="hh font-semibold text-gray-700">
            <FormattedMessage id="JobMap" defaultMessage="JobMap" />
          </h1>
          <Buttons color="#b71b1b" text="Close" onBtnClick={onClose} />
        </div>

        <div className="flex-1 p-2">
          <APIProvider apiKey={API.MAP_KEY}>
            <Map
              id="job-list-map"
              ref={mapRef}
              defaultZoom={14}
              defaultCenter={currentLocation}
              style={{ width: '100%', height: '100%', borderRadius: '8px' }}
              gestureHandling={'greedy'}
              disableDefaultUI={false}
              fullscreenControl={false}
              mapId={API.MAP_KEY}
              onTilesLoaded={(ev) => {
                if (ev?.map && onMapReady) {
                  onMapReady(ev.map, shouldFitOnReady);
                }
              }}
              onClick={() => {
                if (showingInfoWindow) {
                  onInfoWindowClose();
                }
              }}
              onCameraChanged={(ev) => {
                if (ev?.map && onMapReady) {
                  onMapReady(ev.map, false);
                }
              }}
            >
              <AdvancedMarker position={currentLocation}>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: '#4285F4',
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                />
              </AdvancedMarker>

              {geocodedJobs.map((job, index) => {
                const MapDnDTaskItem = mapTaskDndSource.getDragSource();
                return (
                  <AdvancedMarker
                    key={`${job.id}-${index}`}
                    position={job.geocodedCoords}
                    draggable={true}
                    onClick={() => onMarkerClick(job)}
                    onDragStart={(e) => {
                      startGlobalDrag(job, e);
                    }}
                  >
                    <div
                      ref={(el) => (markerRefs[job.id] = el)}
                      style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: getMarkerColor(job.type),
                        border: '2px solid white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        cursor: 'grab',
                        position: 'relative',
                        zIndex: 1000,
                      }}
                      title={`${job.customerName} - ${job.type}`}
                    />
                    {MapDnDTaskItem && (
                      <div style={{ display: 'none' }}>
                        <MapDnDTaskItem
                          task={job}
                          handleClickJobListItem={handleClickJobListItem}
                          toggleCoachmarksVisibility={
                            toggleCoachmarksVisibility
                          }
                          currentCoachmarkTarget={currentCoachmarkTarget}
                        />
                      </div>
                    )}
                  </AdvancedMarker>
                );
              })}

              {showingInfoWindow && selectedPlace && (
                <InfoWindow
                  position={selectedPlace.geocodedCoords}
                  onCloseClick={onInfoWindowClose}
                >
                  <div
                    className="job-details-hover-text"
                    style={{
                      backgroundColor: '#333',
                      color: '#fff',
                      padding: '10px',
                      borderRadius: '5px',
                      minWidth: '200px',
                      position: 'relative',
                    }}
                  >
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      <li style={{ marginBottom: '5px' }}>
                        Name:- {selectedPlace?.customerName}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Id:- {selectedPlace?.jobId}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Category:- {selectedPlace?.reportCategory}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Type:- {selectedPlace?.type}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Frequency:- {selectedPlace?.frequency}
                      </li>
                      <li style={{ marginBottom: '5px' }}>
                        Description:- {selectedPlace?.description}
                      </li>
                    </ul>
                  </div>
                </InfoWindow>
              )}
            </Map>
          </APIProvider>
        </div>
      </div>
    </Rnd>,
    document.body
  );
};

FloatingMap.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  currentLocation: PropTypes.object,
  geocodedJobs: PropTypes.array,
  selectedPlace: PropTypes.object,
  showingInfoWindow: PropTypes.bool,
  onMarkerClick: PropTypes.func,
  onInfoWindowClose: PropTypes.func,
  getMarkerColor: PropTypes.func,
  markerRefs: PropTypes.object,
  startGlobalDrag: PropTypes.func,
  mapTaskDndSource: PropTypes.object,
  handleClickJobListItem: PropTypes.func,
  toggleCoachmarksVisibility: PropTypes.func,
  currentCoachmarkTarget: PropTypes.string,
  onMapReady: PropTypes.func,
  shouldFitOnReady: PropTypes.bool,
};

export default FloatingMap;
